import { notification } from "antd";
import cuid from "cuid";
import { get, uniq } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { NAVIGATION_DIRECTIONS, USER_TYPES } from "utils/constants";
import { expandSkillsForApi } from "../shared/userSkills";
import { resolveSupervisorAssignment } from "./supervisorPoles";
import View from "./View";

const Detail = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.USER);
  const FORM = t("FORM", { returnObjects: true });
  const navigate = useNavigate();
  const { id } = useParams();
  const cancel = () => navigate(NAVIGATION_DIRECTIONS.BACK);
  const [getSupervisors] = useLazyQuery(graphql.queries.USERS);
  const [addPole] = useMutation(graphql.mutations.ADD_POLE);
  const [deletePole] = useMutation(graphql.mutations.DELETE_POLE, {
    // Rollback only. The failed assignment is already reported.
    onError: () => undefined,
  });
  const [linkSupervisorPole] = useMutation(graphql.mutations.UPDATE_USER);

  const [updateUser] = useMutation(graphql.mutations.UPDATE_USER, {
    refetchQueries: [
      {
        query: graphql.queries.USER,
        awaitRefetchQueries: true,
        variables: {
          where: { id },
        },
      },
    ],
    onCompleted: () => {
      navigate(NAVIGATION_DIRECTIONS.BACK, { replace: true });
    },
  });
  const { data, loading, error } = useQuery(graphql.queries.USER, {
    variables: { where: { id } },
  });

  const onSubmit = async ({ supervisors, skills, ...formData }) => {
    const supervisorIds = supervisors || [];
    const organizationId = get(formData, "organization.id");
    let poleIds = [];

    if (supervisorIds.length) {
      const { data: supervisorsResult } = await getSupervisors({
        variables: {
          where: {
            meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type },
          },
          isIn: { id: supervisorIds },
          take: supervisorIds.length,
        },
      });
      const assignment = resolveSupervisorAssignment(
        supervisorIds,
        get(supervisorsResult, "users.data", []),
      );
      const cannotCreateMissingPoles =
        !organizationId && assignment.supervisorIdsWithoutPole.length > 0;
      if (!assignment.canAssign || cannotCreateMissingPoles) {
        notification.error({ message: FORM.SUPERVISORS_UNRESOLVED });
        return;
      }

      poleIds = [...assignment.poleIds];

      for (const supervisorId of assignment.supervisorIdsWithoutPole) {
        const poleId = cuid();
        let poleCreated = false;
        try {
          await addPole({
            variables: {
              data: {
                id: poleId,
                organization: { id: organizationId },
              },
            },
          });
          poleCreated = true;
          await linkSupervisorPole({
            variables: {
              where: { id: supervisorId },
              data: { poles: [{ id: poleId }] },
            },
          });
          poleIds.push(poleId);
        } catch (assignmentError) {
          if (poleCreated) {
            await deletePole({
              variables: { where: { id: poleId } },
            }).catch(() => undefined);
          }
          throw assignmentError;
        }
      }
    }

    return updateUser({
      variables: {
        where: {
          id,
        },
        data: {
          ...formData,
          skills: expandSkillsForApi(skills),
          poles: uniq(poleIds).map((poleId) => ({ id: poleId })),
        },
      },
    });
  };

  if (loading || error) return <Loading />;

  return (
    <View
      onSubmit={onSubmit}
      data={omitDeep(data.user, "__typename")}
      cancel={cancel}
    />
  );
};

export default Detail;
